<template>
  <page-wrapper error-key="documents">
    <template #title>
      <div class="h3">Документы</div>
    </template>

    <div class="mb-6">
      В разделе представлены документы за&nbsp;последний год. Если есть необходимость получить более ранние документы,
      обратитесь в&nbsp;<a class="link" href="/support">Поддержку</a>.
    </div>

    <div class="documents-filters">
      <e-combobox
        v-model="filters.documentType"
        :items="documentTypeFilterItems"
        label="Тип документа"
        multiple
        clearable
        show-menu-icon
      />
      <e-select
        v-model="filters.juridicalPerson"
        :items="juridicalPersonFilterItems"
        label="Юридическое лицо"
        clearable
      />
      <e-select v-model="filters.reportPeriod" :items="reportPeriodFilterItems" label="Отчётный месяц" clearable />
      <e-select v-model="filters.status" :items="statusFilterItems" label="Статус документа" clearable />
    </div>

    <e-data-items
      v-if="isLoading || filteredTableItems.length"
      interactive
      :loading="isLoading"
      :disable-loader="isDocumentLoading"
      :headers="tableHeaders"
      :items="filteredTableItems"
      @on-row-click="openDocumentPreview"
    >
      <template v-slot:[`item.statusString`]="{ item }">
        <status-document :edi-type="item.ediType" :status="item.signatureStatus" />
      </template>
      <template v-slot:[`item.download`]="{ item }">
        <e-btn
          prepend-icon-src="download"
          variant="link"
          :icon="!showDownloadText"
          :loading="loadedDownloadIds.includes(item.downloadId)"
          @click.stop="handleDocumentDownload(item)"
        >
          <template v-if="showDownloadText"> Скачать </template>
        </e-btn>
      </template>
    </e-data-items>
    <page-content-placeholder
      v-else
      :img-src="DocumentsPlaceholderImage"
      text="За выбранный период документы для скачивания не найдены."
      max-width="690px"
    >
      <div class="paragraph-m text-secondary mt-2">
        Если нужные вам документы ещё не сформированы, обратитесь в
        <router-link :to="{ name: RouteNames.Support }" class="link">Поддержку</router-link>
      </div>
    </page-content-placeholder>

    <sign-document-modal
      v-if="documentToSign && documentToSignData"
      :document-to-sign="documentToSign"
      :edi-type="documentToSignData.ediType"
      :signature-status="documentToSignData.signatureStatus"
      @close="closeSignDocumentModal"
      @success="handleSignDocumentSuccess"
      @download="downloadOpenedDocument"
    />

    <document-preview-modal
      v-model="isDocumentPreviewModalOpen"
      :document-name="documentPreviewData?.documentName"
      :blob="documentPreviewData?.blob"
      @close="closeDocumentPreviewModal"
    />
  </page-wrapper>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import { computed, reactive, ref, toRef } from 'vue';
import { useDisplay } from 'vuetify';
import DocumentsPlaceholderImage from '@/assets/img/pages/documents/documents-placeholder.svg';
import PageContentPlaceholder from '@/core/components/common/PageContentPlaceholder.vue';
import EDataItems from '@/core/components/common/data-items/EDataItems.vue';
import PageWrapper from '@/core/components/interface/PageWrapper.vue';
import { EBtn, ECombobox, ESelect } from '@/core/components/ui';
import { useFetch } from '@/core/composables/useFetch';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { toDateString } from '@/core/utils/date';
import { downloadDocument } from '@/core/utils/file';
import { getSelectFilterItems } from '@/core/utils/filters';
import {
  DocumentType,
  EdiType,
  ESignatureStatus,
  type GetDocumentToSignQuery,
  type ReportPeriod,
} from '@/graphql/generated';
import DocumentPreviewModal from '@/parts/documents/components/DocumentPreviewModal.vue';
import SignDocumentModal from '@/parts/documents/components/SignDocumentModal.vue';
import StatusDocument from '@/parts/documents/components/StatusDocument.vue';
import { type DocumentsItemType, useDocumentsStore } from '@/parts/documents/store/documents';
import { DOCUMENT_TYPE_MAP } from '@/parts/documents/utils/consts';

const { documents, getDocuments, getDocumentLink, getDocumentToSign } = useDocumentsStore();

const $display = useDisplay();

const isDocumentPreviewModalOpen = ref(false);

const filters = reactive<{
  reportPeriod: string | null;
  juridicalPerson: string | null;
  documentType: DocumentType[];
  status: string | null;
}>({
  reportPeriod: null,
  juridicalPerson: null,
  documentType: [],
  status: null,
});

const loadedDownloadIds = ref<string[]>([]);

const showDownloadText = toRef(() => !$display.md.value);

const reportPeriodFilterItems = computed(() =>
  documents.value.reduce<Array<{ title: string }>>((result, item) => {
    const itemTitle = getFormattedReportPeriod(item.reportPeriod);

    if (!result.some((resultItem) => resultItem.title === itemTitle)) result.push({ title: itemTitle });
    return result;
  }, []),
);

const statusFilterItems = computed(() =>
  documents.value.reduce<Array<{ title: string; value: string }>>((result, item) => {
    const itemTitle =
      item.ediType === EdiType.Other
        ? 'Стороннее ЭДО'
        : item.signatureStatus === ESignatureStatus.Signed1C
          ? 'Ожидает подписания'
          : 'Подписан';
    const itemValue = item.ediType === EdiType.Other ? (EdiType.Other as string) : (item.signatureStatus as string);

    if (!result.some((resultItem) => resultItem.title === itemTitle)) {
      result.push({ title: itemTitle, value: itemValue });
    }

    return result;
  }, []),
);

const juridicalPersonFilterItems = computed(() =>
  getSelectFilterItems(documents.value, 'juridicalPerson').map((item: DocumentsItemType) => item.juridicalPerson),
);

const documentTypeFilterItems = computed(() =>
  getSelectFilterItems(documents.value, 'documentType').map((item: DocumentsItemType) => ({
    title: DOCUMENT_TYPE_MAP[item.documentType],
    value: item.documentType,
  })),
);

const tableHeaders = computed(() => [
  {
    key: 'documentName',
    title: 'Документ',
  },
  {
    key: 'juridicalPerson',
    title: 'Юр. лицо',
  },
  {
    key: 'formattedReportPeriod',
    title: 'Отчётный месяц',
  },
  {
    key: 'formattedDocumentDate',
    title: 'Дата формирования',
  },
  {
    key: 'statusString',
    title: 'Статус документа',
    sortRaw: (v1: DocumentsItemType, v2: DocumentsItemType) =>
      `${v2.signatureStatus}` > `${v1.signatureStatus}` ? -1 : 1,
  },
  {
    key: 'download',
    sortable: false,
    width: showDownloadText.value ? 160 : 50,
  },
]);

const isDocumentLoading = ref(false);

const tableItems = computed(() =>
  documents.value.map((item) => ({
    ...item,
    formattedReportPeriod: getFormattedReportPeriod(item.reportPeriod),
    formattedDocumentDate: dayjs(item.documentDate).format('D MMM YYYY'),
  })),
);

const filteredTableItems = computed(() =>
  tableItems.value.filter(
    (item) =>
      (!filters.reportPeriod || item.formattedReportPeriod === filters.reportPeriod) &&
      (!filters.juridicalPerson || item.juridicalPerson === filters.juridicalPerson) &&
      (!filters.documentType.length || filters.documentType.includes(item.documentType)) &&
      (!filters.status ||
        (filters.status === EdiType.Other && item.ediType === EdiType.Other) ||
        item.signatureStatus === filters.status),
  ),
);

const getFormattedReportPeriod = (reportPeriod: ReportPeriod) =>
  dayjs(new Date(reportPeriod.year, reportPeriod.month - 1)).format('MMMM YYYY');

const downloadOpenedDocument = () => {
  if (!documentToSignData.value) {
    return;
  }

  void handleDocumentDownload(documentToSignData.value);
};

const handleDocumentDownload = async (item: DocumentsItemType) => {
  loadedDownloadIds.value.push(item.downloadId);

  const result = await getDocumentLink(item.downloadId, item.ediType);

  if (!result.errors && result.data?.getDocumentLink?.data) {
    await downloadDocument(
      result.data.getDocumentLink.data,
      item.fileName,
      item.ediType === EdiType.Other ? 'application/pdf' : 'application/zip',
    );
  }

  loadedDownloadIds.value = loadedDownloadIds.value.filter((id) => id !== item.downloadId);
};

const documentPreviewData = ref<{ documentName: string; blob: Blob } | null>(null);

const documentToSignData = ref<DocumentsItemType | null>(null);
const documentToSign = ref<GetDocumentToSignQuery['documentToSign'] | null>(null);

const openDocumentPreview = async (_e: Event, row: { item: DocumentsItemType }) => {
  isDocumentLoading.value = true;

  if (row.item.ediType === EdiType.Other) {
    documentPreviewData.value = null;

    const result = await getDocumentLink(row.item.downloadId, row.item.ediType);

    if (!result.errors && result.data?.getDocumentLink?.data) {
      documentPreviewData.value = {
        documentName: `${row.item.documentName} от ${toDateString(row.item.documentDate)}`,
        blob: result.data.getDocumentLink.data,
      };

      isDocumentPreviewModalOpen.value = true;
    }

    isDocumentLoading.value = false;
    return;
  }

  const result = await getDocumentToSign(row.item.documentId);

  documentToSignData.value = row.item;
  isDocumentLoading.value = false;

  if (result.data?.documentToSign) {
    documentToSign.value = result.data.documentToSign;
  }
};

const handleSignDocumentSuccess = () => {
  if (documentToSignData.value) {
    documentToSignData.value.signatureStatus = ESignatureStatus.MutuallySigned;
  }
};

const closeSignDocumentModal = () => {
  documentToSign.value = null;
  documentToSignData.value = null;
};

const closeDocumentPreviewModal = () => {
  documentPreviewData.value = null;
  isDocumentPreviewModalOpen.value = false;
};

const { isLoading } = useFetch(getDocuments);
</script>

<style scoped lang="scss">
.documents-filters {
  display: grid;

  grid-gap: 16px;

  margin-bottom: 24px;

  @include sm-and-up {
    grid-template-columns: repeat(2, 1fr);
  }

  @include md-and-up {
    grid-template-columns: repeat(4, 1fr);
  }
}
</style>
